import type { LabeebTenantSummary } from "../../api/labeeb";
import { searchOnlyValue, type McpTenantSummary } from "../../api/mcp";

/**
 * One merchant, as both products see it.
 *
 * `mcp` is null for a merchant only Labeeb knows. That is the whole point of
 * this type: the list used to iterate MCP's response alone, so a merchant with
 * a Labeeb config item and no MCP history was ABSENT from the console — not
 * shown as inactive, absent — and nothing on the page could say so.
 */
export interface MergedTenant {
  reference: string;
  mcp: McpTenantSummary | null;
  labeeb: LabeebTenantSummary | null;
}

/**
 * Every merchant either product knows, once each.
 *
 * MCP's rows come first and keep their order — it is the larger list and the
 * one with names — then any Labeeb reference MCP has never seen.
 *
 * THE JOIN KEY is MCP's `business_reference` ↔ Labeeb's OWN
 * `business_reference`, falling back to its `tenant_id`.
 *
 * This used to be `tenant_id` alone, with a note admitting nothing guaranteed
 * the two were one namespace. They need not be assumed any more: Labeeb's list
 * route does a BatchGetItem against the BusinessProfile table and attaches the
 * real `business_reference` — the same identifier Foodics reports on /whoami,
 * which is what MCP keys on. The fallback stays because that profile write is
 * best-effort and swallowed on failure, so a healthy tenant can arrive without
 * one.
 */
export function mergeTenants(
  mcp: McpTenantSummary[],
  labeeb: LabeebTenantSummary[],
): MergedTenant[] {
  // The reference Labeeb itself reports, else its tenant id.
  //
  // THE REFERENCE MOVED. It used to be a top-level `business_reference` on
  // every list row; the new API nests identity under `profile` and names it
  // `bi_business_id`. The fallback to `tenant_id` matters more than before,
  // because `profile` is explicitly NULLABLE on `TenantOut` — a business can
  // be listed before its profile resolves.
  const keyOf = (t: LabeebTenantSummary): string =>
    String(t?.profile?.bi_business_id || t?.tenant_id || "");

  const byRef = new Map<string, LabeebTenantSummary>();
  for (const t of labeeb) {
    const key = keyOf(t);
    if (key) byRef.set(key, t);
  }

  const seen = new Set<string>();
  const merged: MergedTenant[] = [];

  for (const m of mcp) {
    const reference = String(m.business_reference);
    seen.add(reference);
    merged.push({ reference, mcp: m, labeeb: byRef.get(reference) ?? null });
  }

  for (const t of labeeb) {
    const reference = keyOf(t);
    // A merchant present in both is already in the list with its MCP half.
    // Adding it again here is the bug this guard exists for — it would show
    // the same merchant twice, once without a name.
    if (!reference || seen.has(reference)) continue;
    seen.add(reference);
    merged.push({ reference, mcp: null, labeeb: t });
  }

  return merged;
}

/**
 * The merchant's name, from whichever product knows it, or null.
 *
 * MCP first because its list is the one with names for most of the fleet, then
 * Labeeb's — which the console used to drop entirely, rendering a Labeeb-only
 * merchant as an em dash and a "Labeeb only" badge under the belief that
 * Labeeb's list carried no name. It does: the admin API attaches it from the
 * BusinessProfile item.
 *
 * NULL rather than `""` when neither has one, so a caller renders a dash
 * deliberately instead of an empty cell that reads as a layout bug. Absence is
 * normal, not broken — the profile write is an observational side effect of
 * session resolution and its failures are swallowed.
 */
export function nameOf(t: MergedTenant): string | null {
  return t.mcp?.business_name || t.labeeb?.profile?.business_name || null;
}

/**
 * Text to match a filter against — every identifier the estate keys on.
 *
 * The same merchant is keyed four ways, and an operator arriving from any one
 * store holds whichever key that store uses (S0.1 / DA-3335):
 *
 *   business_reference   MCP blocklist — what ops actually say out loud
 *   business name        what a human searches for
 *   tenant_id            Labeeb's own key, NOT the business reference
 *   business_id          the entitlements table's UUID
 *
 * Owner email is matched too, since support arrives with one often enough.
 *
 * `business_id` is MATCHED HERE AND RENDERED NOWHERE. This is the single site
 * that unwraps a `SearchOnly` value, which is what makes the brand on that
 * type worth having — see the docstring in `api/mcp.ts`. Accepting a UUID
 * pasted from a DynamoDB console costs nothing; putting one on screen beside
 * a reference number invites copying the wrong one into a ticket.
 *
 * It used to read MCP's fields alone, so a Labeeb-only merchant was findable
 * by reference and nothing else.
 */
export function searchTextOf(t: MergedTenant): string {
  // Deduplicated and compacted. Labeeb's `tenant_id` IS the reference for
  // most merchants, so joining blindly repeated it and padded the string with
  // empty segments — harmless to `includes`, but it made the haystack a
  // misleading thing to assert on, and a test rightly caught it.
  const parts = [
    t.reference,
    nameOf(t) ?? "",
    t.mcp?.email ?? "",
    t.labeeb?.tenant_id ?? "",
    searchOnlyValue(t.mcp?.business_id),
  ].filter((p) => p !== "");

  return [...new Set(parts)].join(" ").toLowerCase();
}

/** The stored MCP tier, or null — including for a merchant MCP has never seen. */
export function mcpTierOf(t: MergedTenant): string | null {
  return t.mcp?.tier ?? null;
}

/**
 * Is Labeeb suspended for this merchant?
 *
 * REPLACES `labeebTierOf`. The fleet list no longer carries a tier — it is
 * its own document per business (`GET /admin/tenant/{id}/quota`), and
 * fetching one per row to fill a column would be N requests to answer a
 * question nobody scans a list for. Standing IS on the row, and it is the
 * thing worth seeing at a glance.
 */
export function labeebSuspended(t: MergedTenant): boolean {
  return t.labeeb?.status === "suspended";
}
